import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon, ChevronsLeft, ChevronsRight, Inbox, Search,
  Sparkles, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import NoteCard from './NoteCard';
import NoteHistoryModal from './NoteHistoryModal';
import { supabase } from '../../../lib/supabaseClient';
import '../../../styles/KnowledgePanel.css';

// Latin → Persian digit conversion. Kept local so this panel doesn't pull
// in a shared util; if a third consumer in this folder needs it, lift.
const FA_NUMERALS = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (n) => String(n ?? 0).replace(/\d/g, (d) => FA_NUMERALS[Number(d)]);

const ACTIVE_STATUSES = new Set(['pending', 'running']);
const PAGE_SIZE = 20;

// Three filter modes mapped to the backend's query params. Each tuple is
// [id, label, queryParams]. The `query` is what we POST to the API; the
// id is the React state value; the label is what the segment shows.
const FILTERS = [
  { id: 'active',   label: 'فعال',         query: { status: 'active' } },
  { id: 'flagged',  label: 'تناقض‌دارها',  query: { status: 'active', flagged: 'true' } },
  { id: 'archived', label: 'بایگانی‌شده',  query: { status: 'archived' } },
];

// ────────────────────────────────────────────────────────────────────────
// Status panel: progress bar + percentage + counters for the running job
// ────────────────────────────────────────────────────────────────────────
function describeJob(job) {
  if (!job) return null;
  if (job.status === 'pending') {
    return { kind: 'pending', text: 'در صف اجرا…', pct: null, counts: null };
  }
  if (job.status === 'running') {
    const split   = job.split   ?? 0;
    const merged  = job.merged  ?? 0;
    const flagged = job.flagged ?? 0;
    const total = job.progress_total ?? 0;
    const done  = job.progress_done  ?? 0;
    const pct = total > 0 ? Math.min(99, Math.floor((done / total) * 100)) : null;
    return {
      kind: 'running',
      text: total > 0
        ? `در حال مرتب‌سازی هوشمند… مرحلهٔ ${toFa(done)} از ${toFa(total)}`
        : 'در حال مرتب‌سازی هوشمند… (آماده‌سازی خوشه‌ها)',
      hint: 'می‌توانید این صفحه را ببندید و بعداً برگردید - پیشرفت روی سرور ادامه دارد.',
      pct,
      counts: { split, merged, flagged },
    };
  }
  if (job.status === 'completed') {
    return {
      kind: 'completed',
      text: 'مرتب‌سازی تکمیل شد',
      pct: 100,
      counts: {
        split:   job.split   ?? 0,
        merged:  job.merged  ?? 0,
        flagged: job.flagged ?? 0,
      },
    };
  }
  if (job.status === 'failed') {
    return { kind: 'failed', text: `خطا: ${job.error || 'مرتب‌سازی ناموفق بود.'}`, pct: null, counts: null };
  }
  return null;
}

function DreamingStatus({ job }) {
  const view = describeJob(job);
  if (!view) return null;
  const barColor =
    view.kind === 'failed'    ? '#dc2626' :
    view.kind === 'completed' ? '#16a34a' :
                                '#2563eb';
  return (
    <div style={{
      marginTop: 12, padding: '10px 14px',
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
      fontSize: 13, color: '#1e293b',
    }}>
      {view.pct !== null && (
        <div style={{
          height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden',
          marginBottom: 8,
        }}>
          <div style={{
            width: `${view.pct}%`, height: '100%', background: barColor,
            transition: 'width 320ms ease',
          }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontWeight: 600,
          color: view.kind === 'failed' ? '#b91c1c'
               : view.kind === 'completed' ? '#15803d'
               : '#1e293b',
        }}>
          {view.text}
        </span>
        {view.pct !== null && (
          <span style={{ fontSize: 12, color: '#64748b', fontFeatureSettings: '"tnum"' }}>
            {toFa(view.pct)}٪
          </span>
        )}
      </div>
      {view.counts && (
        <div style={{
          marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6,
          fontSize: 12, color: '#475569',
        }}>
          <Chip label="تجزیه"        value={view.counts.split}   accent="#2563eb" />
          <Chip label="ادغام"        value={view.counts.merged}  accent="#15803d" />
          <Chip label="علامت‌گذاری" value={view.counts.flagged} accent="#b45309" />
        </div>
      )}
      {view.hint && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}>
          {view.hint}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, accent }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 4,
      padding: '2px 9px', borderRadius: 999,
      background: '#fff', border: `1px solid ${accent}33`, color: accent,
    }}>
      <span style={{ fontSize: 11 }}>{label}</span>
      <strong style={{ fontFeatureSettings: '"tnum"' }}>{toFa(value)}</strong>
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Filter pill — 3-segment toggle
// ────────────────────────────────────────────────────────────────────────
// Earlier draft used a JS-measured sliding indicator div. Two problems:
//   (1) On first paint useLayoutEffect hadn't run yet, so the indicator
//       sat at width=0 while the selected segment was already styled
//       color:white → "فعال" rendered as white-on-white (invisible)
//       until the user changed the filter once.
//   (2) Font Vazirmatn loads async; the first measurement was against
//       the fallback font and could end up too narrow.
// Both bugs go away if we drop the indicator entirely and put the
// selected background ON the segment itself. data-active on the
// container drives the accent colour for the flagged variant.
function FilterPill({ value, onChange }) {
  return (
    <div className="kp-filter" data-active={value} role="tablist">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          className="kp-filter-seg"
          role="tab"
          aria-selected={value === f.id}
          onClick={() => onChange(f.id)}
          type="button"
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Pagination footer — minimal, accessible
// ────────────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <nav className="kp-pagination" aria-label="صفحه‌بندی نتایج">
      <span className="kp-page-label">
        صفحهٔ <strong>{toFa(page)}</strong> از <strong>{toFa(totalPages)}</strong>
      </span>
      <div className="kp-page-controls">
        {/* RTL: chevrons point in the direction the page index moves.
            Persian reads right-to-left, so "older" (lower-numbered) is
            visually on the right; ChevronRight = prev. */}
        <button
          type="button" className="kp-page-btn"
          aria-label="صفحهٔ اول" disabled={page <= 1}
          onClick={() => onChange(1)}
        ><ChevronsRight size={16} /></button>
        <button
          type="button" className="kp-page-btn"
          aria-label="صفحهٔ قبل" disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        ><ChevronRight size={16} /></button>
        <button
          type="button" className="kp-page-btn"
          aria-label="صفحهٔ بعد" disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        ><ChevronLeft size={16} /></button>
        <button
          type="button" className="kp-page-btn"
          aria-label="صفحهٔ آخر" disabled={page >= totalPages}
          onClick={() => onChange(totalPages)}
        ><ChevronsLeft size={16} /></button>
      </div>
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────────────
export default function KnowledgePanel({ agent }) {
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [historyNote, setHistoryNote] = useState(null);
  const [job, setJob] = useState(null);
  const channelRef = useRef(null);

  // Debounce: 280ms between keystroke and refetch is a good felt-balance
  // between snappy and not-spammy on the backend.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 280);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Filter changes reset the page IN THE SAME event-handler batch as the
  // filter setter (see onFilterChange below). Without that batch, the
  // first render after a filter switch keeps the stale `page`, which
  // triggers a fetch with the OLD offset — and if the new filter has
  // fewer rows than that offset, supabase .range(N,N+19) returns a 416
  // ("range not satisfiable") that the route converts to 500.
  //
  // The leftover useEffect resets ONLY for debounced-search transitions,
  // where the change happens via a delayed setState and can't be batched
  // with the keystroke. The same stale-offset risk exists there, but
  // typing usually starts on page 1 so the real-world impact is low.
  // The server clamp below catches anything we miss here.
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Single setter that swaps filter AND resets page atomically. React 18
  // batches both setStates inside an event handler into one re-render,
  // so the query useMemo recomputes with offset=0 BEFORE any fetch fires.
  const onFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setPage(1);
  }, []);

  // Build the query object once per state change.
  const query = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    return {
      ...f.query,
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
  }, [filter, debouncedSearch, page]);

  const reload = useCallback(() => agent.loadNotes(query), [agent, query]);

  // Single effect that fires on any query change. Re-using `reload` would
  // also work but the indirection saves nothing here and adds a callback
  // re-creation cycle.
  useEffect(() => {
    agent.loadNotes(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const total = agent.notesTotal ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIndex  = Math.min(page * PAGE_SIZE, total);

  // ── Dreaming-job subscription ───────────────────────────────────────
  const watchJob = useCallback((jobId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    const ch = supabase
      .channel(`dreaming_job_${jobId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'dreaming_jobs', filter: `id=eq.${jobId}` },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          setJob(row);
          if (!ACTIVE_STATUSES.has(row.status)) {
            supabase.removeChannel(ch);
            if (channelRef.current === ch) channelRef.current = null;
            if (row.status === 'completed') reload();
          }
        })
      .subscribe();
    channelRef.current = ch;

    // One-shot catch-up read includes the new progress columns so a tab
    // reopen lands at the correct progress-bar fill.
    supabase.from('dreaming_jobs')
      .select('id, status, split, merged, flagged, progress_total, progress_done, error, created_at, started_at, completed_at')
      .eq('id', jobId).maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setJob((prev) => prev && prev.id === data.id ? { ...prev, ...data } : data);
        if (!ACTIVE_STATUSES.has(data.status)) {
          supabase.removeChannel(ch);
          if (channelRef.current === ch) channelRef.current = null;
          if (data.status === 'completed') reload();
        }
      });
  }, [reload]);

  useEffect(() => {
    (async () => {
      try {
        const { job: active } = await agent.api.getActiveDreamingJob();
        if (active) {
          setJob(active);
          watchJob(active.id);
        }
      } catch { /* best-effort */ }
    })();
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dream = async () => {
    const result = await agent.runDreaming(null);
    if (!result?.job_id) return;
    setJob({ id: result.job_id, status: result.status || 'pending' });
    watchJob(result.job_id);
  };

  const isRunning = job && ACTIVE_STATUSES.has(job.status);

  // ── Empty-state copy depends on context ────────────────────────────
  let emptyView = null;
  if (agent.notes.length === 0) {
    if (debouncedSearch) {
      emptyView = {
        title: `نتیجه‌ای برای «${debouncedSearch}» یافت نشد`,
        sub: 'عبارت جستجو را تغییر دهید یا فیلتر را عوض کنید.',
        icon: <Search size={20} />,
      };
    } else if (filter === 'flagged') {
      emptyView = {
        title: 'هیچ نتِ تناقض‌داری وجود ندارد',
        sub: 'وقتی پاس مرتب‌سازی یا ادمین تناقضی را شناسایی کند، آن نت اینجا نمایش داده می‌شود.',
        icon: <AlertOctagon size={20} />,
      };
    } else if (filter === 'archived') {
      emptyView = {
        title: 'بایگانی خالی است',
        sub: 'نت‌های آرشیوشده (از طریق حذف یا ادغام) اینجا نمایش داده می‌شوند.',
        icon: <Inbox size={20} />,
      };
    } else {
      emptyView = {
        title: 'پایگاه دانش خالی است',
        sub: 'با چت آموزش متنی یا صوتی نت‌های جدید بسازید.',
        icon: <Inbox size={20} />,
      };
    }
  }

  return (
    <div className="consultant-panel">
      {/* ─── Toolbar ──────────────────────────────────────────────────── */}
      <div className="kp-toolbar">
        <FilterPill value={filter} onChange={onFilterChange} />

        <div className="kp-search" role="search">
          <Search size={16} className="kp-search-icon" aria-hidden />
          <input
            type="search"
            className="kp-search-input"
            placeholder="جستجو در متن نت‌ها…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="جستجو در پایگاه دانش"
          />
          {searchTerm && (
            <button
              type="button"
              className="kp-search-clear"
              aria-label="پاک‌کردن جستجو"
              onClick={() => setSearchTerm('')}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          type="button"
          className="kp-dream-btn"
          onClick={dream}
          disabled={agent.busy || isRunning}
          title={isRunning ? 'یک مرتب‌سازی هم‌اکنون در حال اجرا است' : 'اجرای مرتب‌سازی هوشمند'}
        >
          <Sparkles size={15} />
          مرتب‌سازی دانش
        </button>
      </div>

      <DreamingStatus job={job} />

      {/* ─── Result-count line ────────────────────────────────────────── */}
      {total > 0 && (
        <div className="kp-count-line">
          <span>
            نمایش <span className="kp-count-strong">{toFa(firstIndex)}</span>
            {firstIndex !== lastIndex && (
              <> تا <span className="kp-count-strong">{toFa(lastIndex)}</span></>
            )}
            <> از <span className="kp-count-strong">{toFa(total)}</span></>
          </span>
          {debouncedSearch && (
            <>
              <span className="kp-count-divider">·</span>
              <span>جستجو: «{debouncedSearch}»</span>
            </>
          )}
        </div>
      )}

      {/* ─── List or empty state ──────────────────────────────────────── */}
      {emptyView ? (
        <div className="kp-empty">
          <div className="kp-empty-icon">{emptyView.icon}</div>
          <div className="kp-empty-title">{emptyView.title}</div>
          <div className="kp-empty-sub">{emptyView.sub}</div>
        </div>
      ) : (
        agent.notes.map((n, i) => (
          <div
            key={n.id}
            className="kp-note-row"
            style={{ animationDelay: `${Math.min(i, 8) * 18}ms` }}
          >
            <NoteCard
              note={n}
              agent={agent}
              onChanged={reload}
              onShowHistory={setHistoryNote}
            />
          </div>
        ))
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {historyNote && (
        <NoteHistoryModal
          note={historyNote} agent={agent}
          onClose={() => setHistoryNote(null)} onChanged={reload}
        />
      )}
    </div>
  );
}
